import React, { useMemo, useState } from 'react';
import { Card, Col, DatePicker, Input, Modal, Row, Select, Space, Switch, Tag, Tooltip, Typography, message } from 'antd';
import type { ColumnType } from 'antd/es/table';
import dayjs from 'dayjs';
import { LeftOutlined, PlusOutlined, ReloadOutlined, StopOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import StandardTable, { createActionColumn } from '../../../components/common/StandardTable';
import { fetchSqlDataWithQuery } from '../../../services/api';
import { useGetMatLogisticShipments, useGetUnlocodeCountries } from '../../../hooks/genericHooks';
import type { MatLogisticShipment } from '../../../types';
import { CgAttachment } from 'react-icons/cg';

import { updateData } from '../../../services/api';
import Button from '../../../components/common/Button';

const { Title } = Typography;

const RCgAttachment: React.FC<any> = (props) =>
  React.createElement(CgAttachment as unknown as React.ComponentType<any>, props) as React.ReactElement;

const formatEnumString = (value: string | undefined | null): string => {
  if (!value) return '-';
  return value.charAt(0).toUpperCase() + value.slice(1);
};

const getStatusColor = (status: string | undefined | null): string => {
  switch (status?.toLowerCase()) {
    case 'active':
      return 'success';
    case 'passive':
      return 'default';
    case 'cancelled':
      return 'error';
    default:
      return 'default';
  }
};

const getDerivedShipmentStatus = (r: MatLogisticShipment): string => {
  const status = String((r as any)?.status ?? '').trim().toLowerCase();
  if (status === 'cancelled') return 'cancelled';
  if (status === 'passive') return 'passive';
  const arrivalStatus = String((r as any)?.arrival_status ?? '').trim().toLowerCase();
  if (arrivalStatus === 'arrived') return 'passive';
  if (status) return status;
  return 'active';
};

const MatLogShipmentList: React.FC = () => {
  const navigate = useNavigate();
  const { data = [], isLoading, refetch } = useGetMatLogisticShipments() as any;
  const shipments: MatLogisticShipment[] = Array.isArray(data) ? data : (data?.data ?? []);
  const { data: countriesData } = useGetUnlocodeCountries() as any;

  const [showCancelled, setShowCancelled] = useState(false);

  const [showFilters, setShowFilters] = useState(true);
  const [filters, setFilters] = useState<{
    shipment_type?: string;
    transport_type?: string;
    arrival_status?: string;
    departure_country_code?: string;
    destination_country_code?: string;
    planned_from?: string;
    planned_to?: string;
    q?: string;
  }>({});

  const norm = (v?: any) => (v === null || v === undefined ? '' : String(v)).trim();

  const countryNameByCode = useMemo(() => {
    const rows = (countriesData?.data ?? countriesData ?? []) as Array<any>;
    const out: Record<string, string> = {};
    (rows || []).forEach((r) => {
      const cc = String(r?.country_code ?? r?.countryCode ?? '').trim().toUpperCase();
      const name = String(r?.country_name ?? r?.countryName ?? '').trim();
      if (cc && name) out[cc] = name;
    });
    return out;
  }, [countriesData]);

  const departureCountryFilterOptions = useMemo(() => {
    const rows = showCancelled
      ? (shipments || [])
      : (shipments || []).filter((r) => getDerivedShipmentStatus(r) !== 'cancelled');

    const codes = new Set<string>();
    (rows || []).forEach((r) => {
      const dep = String((r as any)?.departure_country_code ?? '').trim().toUpperCase();
      if (dep) codes.add(dep);
    });

    const out = Array.from(codes).map((cc) => {
      const name = countryNameByCode[cc];
      return {
        label: name ? `${name} (${cc})` : cc,
        value: cc,
      };
    });
    out.sort((a, b) => a.label.localeCompare(b.label));
    return out;
  }, [countryNameByCode, shipments, showCancelled]);

  const destinationCountryFilterOptions = useMemo(() => {
    const rows = showCancelled
      ? (shipments || [])
      : (shipments || []).filter((r) => getDerivedShipmentStatus(r) !== 'cancelled');

    const codes = new Set<string>();
    (rows || []).forEach((r) => {
      const dest = String((r as any)?.destination_country_code ?? '').trim().toUpperCase();
      if (dest) codes.add(dest);
    });

    const out = Array.from(codes).map((cc) => {
      const name = countryNameByCode[cc];
      return {
        label: name ? `${name} (${cc})` : cc,
        value: cc,
      };
    });
    out.sort((a, b) => a.label.localeCompare(b.label));
    return out;
  }, [countryNameByCode, shipments, showCancelled]);

  const normalizeLocForLookup = React.useCallback((countryCodeRaw: any, locCodeRaw: any) => {
    const cc = String(countryCodeRaw || '').trim().toUpperCase();
    const lc = String(locCodeRaw || '').trim().toUpperCase();
    if (!cc || !lc) return { cc, lc: '' };
    // Handle case where shipment stores full UN/LOCODE (e.g. TRIST) but MV stores location_code only (e.g. IST)
    if (lc.length === 5 && lc.startsWith(cc)) {
      return { cc, lc: lc.slice(2) };
    }
    return { cc, lc };
  }, []);

  const filteredData = useMemo(() => {
    const q = (filters.q || '').trim().toLowerCase();
    return (shipments || []).filter((r) => {
      const status = getDerivedShipmentStatus(r);
      if (!showCancelled && status === 'cancelled') return false;

      if (filters.shipment_type && norm(r.shipment_type) !== norm(filters.shipment_type)) return false;
      if (filters.transport_type && norm(r.transport_type) !== norm(filters.transport_type)) return false;
      if (filters.arrival_status && norm(r.arrival_status) !== norm(filters.arrival_status)) return false;
      if (filters.departure_country_code && norm(r.departure_country_code) !== norm(filters.departure_country_code)) return false;
      if (filters.destination_country_code && norm(r.destination_country_code) !== norm(filters.destination_country_code)) return false;

      if (filters.planned_from || filters.planned_to) {
        const d = r.planned_departure_date ? String(r.planned_departure_date).slice(0, 10) : null;
        if (!d) return false;
        if (filters.planned_from && d < filters.planned_from) return false;
        if (filters.planned_to && d > filters.planned_to) return false;
      }

      if (q) {
        const hay = [
          r.shipment_id,
          r.po_number,
          r.invoice_no,
          r.forwarder_name,
          r.carrier_name,
          r.vessel_name,
          r.container_no,
          r.airwaybill_no,
          r.billofLading_no,
          r.departure_country_code,
          r.departure_loc_code,
          r.departure_port,
          r.destination_country_code,
          r.destination_loc_code,
          r.destination_port,
          r.actual_departure_date,
          r.actual_arrival_date,
          r.upload_path,
        ]
          .map((x) => (x == null ? '' : String(x)))
          .join(' ')
          .toLowerCase();

        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [filters, shipments, showCancelled]);

  const locPairs = useMemo(() => {
    const set = new Set<string>();
    const pairs: Array<{ cc: string; lc: string }> = [];
    const push = (ccRaw: any, lcRaw: any) => {
      const { cc, lc } = normalizeLocForLookup(ccRaw, lcRaw);
      if (!cc || !lc) return;
      const key = `${cc}:${lc}`;
      if (set.has(key)) return;
      set.add(key);
      pairs.push({ cc, lc });
    };
    (filteredData || []).forEach((r) => {
      push((r as any).departure_country_code, (r as any).departure_loc_code);
      push((r as any).destination_country_code, (r as any).destination_loc_code);
    });
    // Keep query size bounded; typical page will be far below this.
    return pairs.slice(0, 500);
  }, [filteredData]);

  const locPairsSig = useMemo(() => locPairs.map((p) => `${p.cc}:${p.lc}`).join('|'), [locPairs]);
  const { data: locRows = [] } = useQuery<any[]>({
    queryKey: ['mv_cor_unece_unlocodes_basic:bulk_pairs', locPairsSig],
    enabled: locPairs.length > 0,
    staleTime: 24 * 60 * 60 * 1000,
    queryFn: async () => {
      const values = locPairs
        .map((p) => {
          const cc = p.cc.replace(/'/g, "''");
          const lc = p.lc.replace(/'/g, "''");
          return `('${cc}','${lc}')`;
        })
        .join(', ');
      // Backend raw-SQL endpoint only allows queries starting with SELECT.
      // So we avoid CTE/VALUES and use a row-constructor IN list instead.
      const sql = `
        select u.country_code, u.location_code, u.loc_name
        from public.mv_cor_unece_unlocodes_basic u
        where (u.country_code, u.location_code) in (${values})
      `;
      try {
        const res = await fetchSqlDataWithQuery(sql);
        return Array.isArray(res) ? res : (Array.isArray((res as any)?.data) ? (res as any).data : []);
      } catch (e) {
        // Fallback silently to codes; avoid spamming users with toasts on list pages.
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn('[MatLogShipmentList] Failed to resolve LOC names', e);
        }
        return [];
      }
    },
  });

  const locNameByKey = useMemo(() => {
    const out: Record<string, string> = {};
    (locRows || []).forEach((r) => {
      const cc = String(r?.country_code || '').trim().toUpperCase();
      const lc = String(r?.location_code || '').trim().toUpperCase();
      const name = String(r?.loc_name || '').trim();
      if (cc && lc && name) out[`${cc}:${lc}`] = name;
    });
    return out;
  }, [locRows]);

  const makeOptions = <K extends keyof MatLogisticShipment>(key: K) => {
    const set = new Set(
      (shipments || [])
        .map((r) => r[key])
        .filter((v) => v !== null && v !== undefined && String(v).trim() !== '') as any
    );
    return Array.from(set)
      .sort((a: any, b: any) => String(a).localeCompare(String(b)))
      .map((v: any) => ({ label: String(v), value: String(v) }));
  };

  const columns: ColumnType<MatLogisticShipment>[] = useMemo(
    () => [
      { title: 'Shipment ID', dataIndex: 'shipment_id', key: 'shipment_id', width: 140 },
      {
        title: 'Att',
        key: 'has_files',
        width: 36,
        align: 'center' as const,
        onHeaderCell: () => ({ style: { width: 36, minWidth: 36, maxWidth: 36 } }),
        onCell: () => ({ style: { width: 36, minWidth: 36, maxWidth: 36 } }),
        render: (_: any, row: MatLogisticShipment) => {
          const has = !!(row as any)?.upload_path && String((row as any).upload_path).trim() !== '';
          return has ? (
            <Tooltip title="Has attachments">
              <RCgAttachment size={16} color="#1677ff" />
            </Tooltip>
          ) : null;
        },
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 110,
        render: (_: any, r: MatLogisticShipment) => {
          const s = getDerivedShipmentStatus(r);
          return <Tag color={getStatusColor(s)}>{formatEnumString(s)}</Tag>;
        },
        exportValue: (r: MatLogisticShipment) => formatEnumString(getDerivedShipmentStatus(r)),
      },
      { title: 'Arrival Status', dataIndex: 'arrival_status', key: 'arrival_status', width: 120 },
      { title: 'Type', dataIndex: 'shipment_type', key: 'shipment_type', width: 100 },
      { title: 'Transport', dataIndex: 'transport_type', key: 'transport_type', width: 100 },
      { title: 'PO', dataIndex: 'po_number', key: 'po_number', width: 120 },
      { title: 'Invoice', dataIndex: 'invoice_no', key: 'invoice_no', width: 120 },
      {
        title: 'Dep. Country',
        dataIndex: 'departure_country_code',
        key: 'departure_country_code',
        width: 160,
        render: (v: any) => {
          const cc = String(v || '').trim().toUpperCase();
          if (!cc) return '';
          const name = countryNameByCode[cc];
          return name ? `${name} (${cc})` : cc;
        },
        exportValue: (r: MatLogisticShipment) => {
          const cc = String((r as any).departure_country_code || '').trim().toUpperCase();
          const name = cc ? countryNameByCode[cc] : '';
          return cc ? (name ? `${name} (${cc})` : cc) : '';
        },
      },
      {
        title: 'Dep. LOC',
        dataIndex: 'departure_loc_code',
        key: 'departure_loc_code',
        width: 220,
        render: (_: any, r: MatLogisticShipment) => {
          const rawCc = String((r as any).departure_country_code || '').trim().toUpperCase();
          const rawLc = String((r as any).departure_loc_code || '').trim().toUpperCase();
          if (!rawLc) return '';
          const { cc, lc } = normalizeLocForLookup(rawCc, rawLc);
          const name = cc && lc ? locNameByKey[`${cc}:${lc}`] : undefined;
          return name ? `${name} (${rawLc})` : rawLc;
        },
        exportValue: (r: MatLogisticShipment) => {
          const rawCc = String((r as any).departure_country_code || '').trim().toUpperCase();
          const rawLc = String((r as any).departure_loc_code || '').trim().toUpperCase();
          const { cc, lc } = normalizeLocForLookup(rawCc, rawLc);
          const name = cc && lc ? locNameByKey[`${cc}:${lc}`] : undefined;
          return rawLc ? (name ? `${name} (${rawLc})` : rawLc) : '';
        },
      },
      { title: 'Dep. Port', dataIndex: 'departure_port', key: 'departure_port', width: 140 },
      {
        title: 'Dest. Country',
        dataIndex: 'destination_country_code',
        key: 'destination_country_code',
        width: 160,
        render: (v: any) => {
          const cc = String(v || '').trim().toUpperCase();
          if (!cc) return '';
          const name = countryNameByCode[cc];
          return name ? `${name} (${cc})` : cc;
        },
        exportValue: (r: MatLogisticShipment) => {
          const cc = String((r as any).destination_country_code || '').trim().toUpperCase();
          const name = cc ? countryNameByCode[cc] : '';
          return cc ? (name ? `${name} (${cc})` : cc) : '';
        },
      },
      {
        title: 'Dest. LOC',
        dataIndex: 'destination_loc_code',
        key: 'destination_loc_code',
        width: 220,
        render: (_: any, r: MatLogisticShipment) => {
          const rawCc = String((r as any).destination_country_code || '').trim().toUpperCase();
          const rawLc = String((r as any).destination_loc_code || '').trim().toUpperCase();
          if (!rawLc) return '';
          const { cc, lc } = normalizeLocForLookup(rawCc, rawLc);
          const name = cc && lc ? locNameByKey[`${cc}:${lc}`] : undefined;
          return name ? `${name} (${rawLc})` : rawLc;
        },
        exportValue: (r: MatLogisticShipment) => {
          const rawCc = String((r as any).destination_country_code || '').trim().toUpperCase();
          const rawLc = String((r as any).destination_loc_code || '').trim().toUpperCase();
          const { cc, lc } = normalizeLocForLookup(rawCc, rawLc);
          const name = cc && lc ? locNameByKey[`${cc}:${lc}`] : undefined;
          return rawLc ? (name ? `${name} (${rawLc})` : rawLc) : '';
        },
      },
      { title: 'Dest. Port', dataIndex: 'destination_port', key: 'destination_port', width: 140 },
      {
        title: 'Planned Dep.',
        dataIndex: 'planned_departure_date',
        key: 'planned_departure_date',
        width: 120,
        render: (v) => (v ? String(v).slice(0, 10) : '-'),
        exportValue: (r: MatLogisticShipment) => (r.planned_departure_date ? dayjs(r.planned_departure_date).format('YYYY-MM-DD') : ''),
      },
      {
        title: 'Planned Arr.',
        dataIndex: 'planned_arrival_date',
        key: 'planned_arrival_date',
        width: 120,
        render: (v) => (v ? String(v).slice(0, 10) : '-'),
        exportValue: (r: MatLogisticShipment) => (r.planned_arrival_date ? dayjs(r.planned_arrival_date).format('YYYY-MM-DD') : ''),
      },
      {
        title: 'Actual Dep.',
        dataIndex: 'actual_departure_date',
        key: 'actual_departure_date',
        width: 120,
        render: (v) => (v ? String(v).slice(0, 10) : '-'),
        exportValue: (r: MatLogisticShipment) => (r.actual_departure_date ? dayjs(r.actual_departure_date).format('YYYY-MM-DD') : ''),
      },
      {
        title: 'Actual Arr.',
        dataIndex: 'actual_arrival_date',
        key: 'actual_arrival_date',
        width: 120,
        render: (v) => (v ? String(v).slice(0, 10) : '-'),
        exportValue: (r: MatLogisticShipment) => (r.actual_arrival_date ? dayjs(r.actual_arrival_date).format('YYYY-MM-DD') : ''),
      },
      { title: 'Forwarder', dataIndex: 'forwarder_name', key: 'forwarder_name', width: 140 },
      { title: 'Carrier', dataIndex: 'carrier_name', key: 'carrier_name', width: 140 },
      {
        title: 'Updated',
        dataIndex: 'updated_at',
        key: 'updated_at',
        width: 115,
        render: (v) => (v ? String(v).slice(0, 10) : '-'),
      },
      createActionColumn<MatLogisticShipment>({
        width: 104,
        showView: false,
        showDelete: false,
        onEdit: (r) => navigate(`/material/logistics/shipments/edit/${encodeURIComponent(r.shipment_id)}`),
        extraRender: (r) => {
          const status = getDerivedShipmentStatus(r);
          const isCancelled = status === 'cancelled';
          const hasDeparted = Boolean((r as any)?.actual_departure_date);

          const onCancel = () => {
            if (isCancelled) {
              message.info('Shipment is already cancelled');
              return;
            }
            if (hasDeparted) {
              message.error('Cannot cancel: shipment already departed');
              return;
            }

            let reason = '';
            Modal.confirm({
              title: `Cancel shipment ${String((r as any)?.shipment_id || '').trim()}?`,
              okText: 'Cancel Shipment',
              okType: 'danger',
              cancelText: 'Close',
              content: (
                <div>
                  <div style={{ marginBottom: 8 }}>
                    Please provide a cancellation reason. This action will unassign all packages currently linked to this shipment.
                  </div>
                  <Input.TextArea
                    autoSize={{ minRows: 3, maxRows: 8 }}
                    placeholder="Cancellation reason (required)"
                    onChange={(e) => {
                      reason = e.target.value;
                    }}
                  />
                </div>
              ),
              onOk: async () => {
                const shipmentId = String((r as any)?.shipment_id || '').trim();
                const trimmed = String(reason || '').trim();
                if (!trimmed) {
                  message.error('Cancellation reason is required');
                  return Promise.reject(new Error('cancel-reason-required'));
                }
                if (!shipmentId) {
                  message.error('Missing shipment_id; cannot cancel');
                  return Promise.reject(new Error('missing-shipment-id'));
                }

                const user = (() => {
                  try {
                    return window.localStorage.getItem('user');
                  } catch {
                    return null;
                  }
                })();

                // 1) Cancel shipment
                await updateData(
                  'mat_logistic_shipments',
                  shipmentId,
                  {
                    status: 'cancelled',
                    cancel_reason: trimmed,
                    cancelled_at: new Date().toISOString(),
                    cancelled_by: user ? String(user) : null,
                  },
                  'shipment_id'
                );

                // 2) Unassign linked packages
                const safeShipmentId = shipmentId.replace(/'/g, "''");
                const sql = `select package_id from public.mat_logistic_packages where shipment_id = '${safeShipmentId}'`;
                const res = await fetchSqlDataWithQuery(sql);
                const rows = Array.isArray(res) ? res : (Array.isArray((res as any)?.data) ? (res as any).data : []);
                const packageIds = (rows || [])
                  .map((x: any) => String(x?.package_id ?? '').trim())
                  .filter((x: string) => x);

                for (const pid of packageIds) {
                  await updateData(
                    'mat_logistic_packages',
                    pid,
                    {
                      shipment_id: null,
                      status: 'pending',
                    },
                    'package_id'
                  );
                }

                message.success(`Shipment cancelled. Unassigned ${packageIds.length} package(s).`);
                await refetch?.();
              },
            });
          };

          return (
            <Tooltip title={isCancelled ? 'Cancelled' : hasDeparted ? 'Departed (cannot cancel)' : 'Cancel shipment'}>
              <Button
                action="edit"
                icon={<StopOutlined />}
                size="small"
                danger
                disabled={isCancelled || hasDeparted}
                onClick={onCancel}
              />
            </Tooltip>
          );
        },
      }),
    ],
    [countryNameByCode, locNameByKey, navigate, normalizeLocForLookup, refetch]
  );

  return (
    <Card bordered={false} bodyStyle={{ padding: 8 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col>
          <Space align="center" size={8}>
            <Button icon={<LeftOutlined />} type="text" aria-label="Back" onClick={() => navigate(-1)} />
            <Title level={4} style={{ margin: 0 }}>
              Logistics Shipments {filteredData?.length ? `(${filteredData.length})` : ''}
            </Title>
            <Space size={6} style={{ marginLeft: 8 }}>
              <span style={{ color: 'rgba(0,0,0,0.65)' }}>Show cancelled</span>
              <Switch checked={showCancelled} onChange={setShowCancelled} />
            </Space>
          </Space>
        </Col>
        <Col>
          <Space>
            <Button action="add" icon={<PlusOutlined />} onClick={() => navigate('/material/logistics/shipments/create')}>
              Create Shipment
            </Button>
            <Button icon={<ReloadOutlined />} onClick={() => refetch?.()}>
              Refresh
            </Button>
            <Button onClick={() => setFilters({})}>Clear Filters</Button>
            <Button onClick={() => setShowFilters((v) => !v)}>
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>
          </Space>
        </Col>
      </Row>

      {showFilters && (
        <Row gutter={[8, 8]} style={{ marginBottom: 8 }}>
          <Col>
            <Select
              allowClear
              showSearch
              placeholder="Type"
              style={{ width: 140 }}
              value={filters.shipment_type}
              options={makeOptions('shipment_type')}
              onChange={(v) => setFilters((p) => ({ ...p, shipment_type: v || undefined }))}
              optionFilterProp="label"
            />
          </Col>
          <Col>
            <Select
              allowClear
              showSearch
              placeholder="Transport"
              style={{ width: 100 }}
              value={filters.transport_type}
              options={makeOptions('transport_type')}
              onChange={(v) => setFilters((p) => ({ ...p, transport_type: v || undefined }))}
              optionFilterProp="label"
            />
          </Col>
          <Col>
            <Select
              allowClear
              showSearch
              placeholder="Arrival Status"
              style={{ width: 120 }}
              value={filters.arrival_status}
              options={makeOptions('arrival_status')}
              onChange={(v) => setFilters((p) => ({ ...p, arrival_status: v || undefined }))}
              optionFilterProp="label"
            />
          </Col>
          <Col>
            <Select
              allowClear
              showSearch
              placeholder="Dep. Country"
              style={{ width: 120 }}
              value={filters.departure_country_code}
              options={departureCountryFilterOptions}
              onChange={(v) => setFilters((p) => ({ ...p, departure_country_code: v || undefined }))}
              optionFilterProp="label"
            />
          </Col>
          <Col>
            <Select
              allowClear
              showSearch
              placeholder="Dest. Country"
              style={{ width: 120 }}
              value={filters.destination_country_code}
              options={destinationCountryFilterOptions}
              onChange={(v) => setFilters((p) => ({ ...p, destination_country_code: v || undefined }))}
              optionFilterProp="label"
            />
          </Col>
          <Col>
            <DatePicker.RangePicker
              allowClear
              placeholder={["Planned From", "Planned To"]}
              style={{ width: 220 }}
              value={
                (filters.planned_from || filters.planned_to)
                  ? [filters.planned_from ? dayjs(filters.planned_from) : null, filters.planned_to ? dayjs(filters.planned_to) : null] as any
                  : undefined
              }
              onChange={(vals) =>
                setFilters((p) => ({
                  ...p,
                  planned_from: vals?.[0] ? vals[0].format('YYYY-MM-DD') : undefined,
                  planned_to: vals?.[1] ? vals[1].format('YYYY-MM-DD') : undefined,
                }))
              }
            />
          </Col>
          <Col>
            <Input
              allowClear
              placeholder="Search shipment/PO/invoice/fwd/carrier/vessel/BOL/LOC..."
              style={{ width: 160 }}
              value={filters.q}
              onChange={(e) => setFilters((p) => ({ ...p, q: e.target.value || undefined }))}
            />
          </Col>
        </Row>
      )}

      <StandardTable<MatLogisticShipment>
        columns={columns}
        dataSource={filteredData}
        rowKey={(r) => String(r.shipment_id)}
        loading={isLoading}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        enableExport
        enableCellTooltips
        scroll={{ x: 'max-content' }}
        exportFileName="mat_logistic_shipments"
      />
    </Card>
  );
};

export default MatLogShipmentList;
